import { memo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showModal } from "../../../features/statusModal/statusModal";
import {
  deleteSupportMessage,
  discardUnsentMessage,
  editSupportMessage,
  sendSupportMessage,
} from "../../../features/supportChat/supportChatSlice";
import { formatClock, formatFullStamp } from "../../../utils/chatTime";
import Dialog from "../../dialog/Dialog";
import DialogButton from "../../dialog/DialogButton";
import AlertTriangleIcon from "../../icons/AlertTriangleIcon";
import DeleteActionIcon from "../../icons/DeleteActionIcon";
import EditActionIcon from "../../icons/EditActionIcon";
import TextArea from "../../inputs/textArea/TextArea";
import MessageBubble, { BubbleAction } from "../messageBubble/MessageBubble";
import styles from "./chatMessage.module.css";

// One support chat message (see MessageBubble), with staff edit/delete on
// support's own messages. A message sent from here carries status "sending"
// or "failed" until the server has it.
const ChatMessage = ({ message, chatId, side, groupStart, groupEnd, timeZone }) => {
  const dispatch = useDispatch();
  const canReply = useSelector((state) =>
    state.user.rolePermissions.includes("reply-user-chats"),
  );

  const [modalMode, setModalMode] = useState(null);
  const [editText, setEditText] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const isDeleted = Boolean(message.isDeleted);
  const delivery = message.status;
  // Staff edit/delete only staff messages the server has, with the reply
  // permission.
  const canMutate = side === "support" && !isDeleted && !delivery && canReply;
  const sentAt = new Date(message.createdAt);

  const openEdit = () => {
    setEditText(message.message || "");
    setModalMode("edit");
  };

  const closeModal = () => {
    setModalMode(null);
    setEditText("");
    setIsSaving(false);
  };

  const failureMessage = (err, fallback) =>
    typeof err === "object" && err !== null && "error" in err ? err.error : fallback;

  const handleSaveEdit = async () => {
    const trimmed = editText.trim();
    if (!trimmed) {
      dispatch(showModal({ message: "يرجى إدخال نص للرسالة", status: "error" }));
      return;
    }
    setIsSaving(true);
    try {
      await dispatch(
        editSupportMessage({ chatId, messageId: message._id, message: trimmed }),
      ).unwrap();
      dispatch(showModal({ message: "تم تحديث الرسالة", status: "success" }));
      closeModal();
    } catch (err) {
      dispatch(
        showModal({ message: failureMessage(err, "تعذر تحديث الرسالة"), status: "error" }),
      );
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    setIsSaving(true);
    try {
      await dispatch(deleteSupportMessage({ chatId, messageId: message._id })).unwrap();
      dispatch(showModal({ message: "تم حذف الرسالة", status: "success" }));
      closeModal();
    } catch (err) {
      dispatch(
        showModal({ message: failureMessage(err, "تعذر حذف الرسالة"), status: "error" }),
      );
      setIsSaving(false);
    }
  };

  return (
    <>
      <MessageBubble
        side={side}
        groupStart={groupStart}
        groupEnd={groupEnd}
        text={message.message}
        deleted={isDeleted}
        edited={Boolean(message.editedAt)}
        time={formatClock(sentAt, timeZone)}
        timeTitle={formatFullStamp(sentAt, timeZone)}
        dateTime={message.createdAt}
        delivery={delivery}
        onRetry={() =>
          dispatch(
            sendSupportMessage({ chatId, message: message.message, clientId: message.clientId }),
          )
        }
        onDiscard={() => dispatch(discardUnsentMessage({ chatId, clientId: message.clientId }))}
        actions={
          canMutate && (
            <>
              <BubbleAction label="تعديل الرسالة" onClick={openEdit}>
                <EditActionIcon />
              </BubbleAction>
              <BubbleAction label="حذف الرسالة" onClick={() => setModalMode("delete")}>
                <DeleteActionIcon />
              </BubbleAction>
            </>
          )
        }
      />

      {modalMode === "edit" && (
        <Dialog
          title="تعديل الرسالة"
          size="md"
          onClose={closeModal}
          footer={
            <>
              <DialogButton variant="secondary" onClick={closeModal} disabled={isSaving}>
                إلغاء
              </DialogButton>
              <DialogButton variant="primary" onClick={handleSaveEdit} disabled={isSaving}>
                {isSaving ? "جارٍ الحفظ…" : "حفظ"}
              </DialogButton>
            </>
          }
        >
          <div className={styles.modalInner}>
            <TextArea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              aria-label="نص الرسالة"
            />
          </div>
        </Dialog>
      )}

      {modalMode === "delete" && (
        <Dialog
          title="حذف الرسالة"
          icon={<AlertTriangleIcon />}
          tone="danger"
          size="sm"
          role="alertdialog"
          onClose={closeModal}
          closeOnEscape={!isSaving}
          closeOnBackdrop={!isSaving}
          footer={
            <>
              <DialogButton variant="secondary" onClick={closeModal} disabled={isSaving}>
                إلغاء
              </DialogButton>
              <DialogButton variant="danger" onClick={handleConfirmDelete} disabled={isSaving}>
                {isSaving ? "جارٍ الحذف…" : "حذف"}
              </DialogButton>
            </>
          }
        >
          هل أنت متأكد من حذف هذه الرسالة؟ لا يمكن التراجع عن هذا الإجراء.
        </Dialog>
      )}
    </>
  );
};

export default memo(ChatMessage);
